import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export const POST_TYPES = [
  "IG_FEED",
  "FB_FEED",
  "LINKEDIN_TEXT_OR_IMAGE",
  "X_TWEET",
  "THREADS_POST",
  "REDDIT_LINK",
  "REDDIT_MEDIA",
  "GBP_UPDATE",
  "IG_CAROUSEL",
  "LINKEDIN_DOC",
  "X_THREAD",
  "THREADS_THREAD",
  "REDDIT_SELF",
  "IG_REEL",
  "IG_STORY",
  "FB_REEL",
  "FB_STORY",
  "TIKTOK_VIDEO",
  "TIKTOK_PHOTO",
  "YOUTUBE_SHORT",
  "YOUTUBE_LONG",
] as const;
export type PostType = (typeof POST_TYPES)[number];

interface PostTypeMeta {
  platform: string;
  label: string;
  tier: 1 | 2 | 3;
}
export const POST_TYPE_META: Record<PostType, PostTypeMeta> = {
  IG_FEED: { platform: "INSTAGRAM", label: "Feed", tier: 1 },
  FB_FEED: { platform: "FACEBOOK", label: "Feed", tier: 1 },
  LINKEDIN_TEXT_OR_IMAGE: { platform: "LINKEDIN", label: "Post", tier: 1 },
  X_TWEET: { platform: "TWITTER", label: "Tweet", tier: 1 },
  THREADS_POST: { platform: "THREADS", label: "Post", tier: 1 },
  REDDIT_LINK: { platform: "REDDIT", label: "Link post", tier: 1 },
  REDDIT_MEDIA: { platform: "REDDIT", label: "Media post", tier: 1 },
  GBP_UPDATE: { platform: "GOOGLEBUSINESS", label: "Update", tier: 1 },
  IG_CAROUSEL: { platform: "INSTAGRAM", label: "Carousel", tier: 2 },
  LINKEDIN_DOC: { platform: "LINKEDIN", label: "Document carousel", tier: 2 },
  X_THREAD: { platform: "TWITTER", label: "Thread", tier: 2 },
  THREADS_THREAD: { platform: "THREADS", label: "Thread", tier: 2 },
  REDDIT_SELF: { platform: "REDDIT", label: "Self post", tier: 2 },
  IG_REEL: { platform: "INSTAGRAM", label: "Reel", tier: 3 },
  IG_STORY: { platform: "INSTAGRAM", label: "Story", tier: 3 },
  FB_REEL: { platform: "FACEBOOK", label: "Reel", tier: 3 },
  FB_STORY: { platform: "FACEBOOK", label: "Story", tier: 3 },
  TIKTOK_VIDEO: { platform: "TIKTOK", label: "Video", tier: 3 },
  TIKTOK_PHOTO: { platform: "TIKTOK", label: "Photo carousel", tier: 3 },
  YOUTUBE_SHORT: { platform: "YOUTUBE", label: "Short", tier: 3 },
  YOUTUBE_LONG: { platform: "YOUTUBE", label: "Long-form", tier: 3 },
};

export interface GenerateRequest {
  projectId?: string;
  brief: string;
  assetIds: string[];
  postTypes: PostType[];
}

export interface GenerateResponse {
  generated: Array<{ postType: PostType; pieceId: string }>;
  failed: Array<{ postType: PostType; error: string }>;
  projectId: string;
}

export function useGeneratePosts(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateRequest) =>
      apiFetch<GenerateResponse>(`/brands/${brandId}/posts/generate`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pieces", brandId] });
      qc.invalidateQueries({ queryKey: ["review", brandId] });
    },
  });
}
